import { TFunction } from 'react-i18next';
import * as Yup from 'yup';
import { Shape } from '../yup_types';
import { IPartnerList } from '../../store/merchnts/merchant-interfaces';
import { isUSCountry } from '../../utils/utilities';

export type IPartner = {
	[PARTNER_FORM_FIELDS.PARTNER_NAME]: string;
	[PARTNER_FORM_FIELDS.PARTNER_TYPE]: string;
	[PARTNER_FORM_FIELDS.FIRST_NAME]: string;
	[PARTNER_FORM_FIELDS.LAST_NAME]: string;
	[PARTNER_FORM_FIELDS.EMAIL]: string;
	[PARTNER_FORM_FIELDS.PHONE]: string | number;
	[PARTNER_FORM_FIELDS.SUPPORT_NAME]?: string;
	[PARTNER_FORM_FIELDS.SUPPORT_LINK]?: string;
	[PARTNER_FORM_FIELDS.SUPPORT_EMAIL]?: string;
	[PARTNER_FORM_FIELDS.SUPPORT_PHONE]?: string | number;
	[PARTNER_FORM_FIELDS.LOGO]?: string;
	[PARTNER_FORM_FIELDS.PRIVACY_POLICY]?: string;
	[PARTNER_FORM_FIELDS.SITE_URL]?: string;
	[PARTNER_FORM_FIELDS.LINE_1]?: string;
	[PARTNER_FORM_FIELDS.LINE_2]?: string;
	[PARTNER_FORM_FIELDS.CITY]?: string;
	[PARTNER_FORM_FIELDS.ZIPCODE]?: string;
	[PARTNER_FORM_FIELDS.COUNTRY]?: string;
	[PARTNER_FORM_FIELDS.STATE]?: string;
	[PARTNER_FORM_FIELDS.TERMS]?: string;
	[PARTNER_FORM_FIELDS.MERCHANT_TERMS]?: string;
	[PARTNER_FORM_FIELDS.TERMS_AND_CONDITIONS]?: string;
	[PARTNER_FORM_FIELDS.CONSENT_LABEL]?: string;
	[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_EMAIL]?: string;
	[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_PHONE]?: string;
	[PARTNER_FORM_FIELDS.REPLY_TO_EMAIL]?: string;
	[PARTNER_FORM_FIELDS.BCC_EMAIL]?: string;
};

export type IPartnerFormattedFields = {
	id?: string,
	name: string,
	type: string | number,
	supportName: string,
	supportLink: string,
	supportEmail: string,
	supportPhone: string,
	logoFile?: string,
	logoUrl?: string,
	privacyPolicyUrl?: string,
	termsAndConditionsUrl?: string,
	merchantTermsAndConditionsUrl?: string,
	termsAndConditions?: string,
	consentLabel?: string,
	siteUrl?: string,
	notificationSenderEmail?: string,
	notificationSenderPhone?: string,
	replyToEmail?: string,
	bccEmail?: string,
	mainContact: {
		firstName: string,
		lastName: string,
		phone: string,
		email: string,
	},
	address: {
		line1?: string,
		line2?: string,
		city?: string,
		country?: string,
		state?: string,
		zipCode?: string,
	}
};

export enum PARTNER_FORM_FIELDS {
	PARTNER_NAME = 'Name',
	PARTNER_TYPE = 'type',
	FIRST_NAME = 'FirstName',
	LAST_NAME = 'LastName',
	EMAIL = 'Email',
	PHONE = 'Phone',
	SUPPORT_NAME = 'SupportName',
	SUPPORT_LINK = 'SupportLink',
	SUPPORT_EMAIL = 'SupportEmail',
	SUPPORT_PHONE = 'SupportPhone',
	LOGO = 'LogoFile',
	PRIVACY_POLICY = 'privacyPolicyUrl',
	SITE_URL = 'siteUrl',
	LINE_1 = 'line1',
	LINE_2 = 'line2',
	CITY = 'city',
	ZIPCODE = 'ZipCode',
	COUNTRY = 'Country',
	STATE = 'State',
	TERMS = 'termsAndConditionsUrl',
	MERCHANT_TERMS = 'MerchantTermsAndConditionsUrl',
	TERMS_AND_CONDITIONS = 'termsAndConditions',
	CONSENT_LABEL = 'consentLabel',
	NOTIFICATION_SENDER_EMAIL = 'notificationSenderEmail',
	NOTIFICATION_SENDER_PHONE = 'notificationSenderPhone',
	REPLY_TO_EMAIL = 'replyToEmail',
	BCC_EMAIL = 'bccEmail',
}

export enum PARTNER_FORM_FIELDS_NAMES {
	PARTNER_NAME = 'PARTNERS.LABEL.PARTNER_NAME',
	PARTNER_TYPE = 'PARTNERS.LABEL.PARTNER_TYPE',
	FIRST_NAME = 'PARTNERS.LABEL.FIRST_NAME',
	LAST_NAME = 'PARTNERS.LABEL.LAST_NAME',
	EMAIL = 'PARTNERS.LABEL.EMAIL',
	PHONE = 'PARTNERS.LABEL.PHONE',
	SUPPORT_NAME = 'PARTNERS.LABEL.SUPPORT_NAME',
	SUPPORT_LINK = 'PARTNERS.LABEL.SUPPORT_LINK',
	SUPPORT_EMAIL = 'PARTNERS.LABEL.SUPPORT_EMAIL',
	SUPPORT_PHONE = 'PARTNERS.LABEL.SUPPORT_PHONE',
	SUPPORT_NAME_TOOLTIP = 'NEW_APPLICATION.LABEL.CUSTOMER_NAME_TOOLTIP',
	SUPPORT_LINK_TOOLTIP = 'NEW_APPLICATION.LABEL.SUPPORT_LINK_TOOLTIP',
	SUPPORT_EMAIL_TOOLTIP = 'NEW_APPLICATION.LABEL.CUSTOMER_EMAIL_TOOLTIP',
	SUPPORT_PHONE_TOOLTIP = 'NEW_APPLICATION.LABEL.CUSTOMER_PHONE_TOOLTIP',
	LOGO = 'NEW_APPLICATION.LABEL.LOGO',
	PRIVACY_POLICY = 'PARTNERS.LABEL.PRIVACY_POLICY',
	SITE_URL = 'PARTNERS.LABEL.SITE_URL',
	LINE_1 = 'NEW_APPLICATION.LABEL.LINE_1',
	LINE_1_PLACEHOLDER = 'OWNER.LINE_1_TOOLTIP_TEXT',
	LINE_2 = 'NEW_APPLICATION.LABEL.LINE_2',
	LINE_2_PLACEHOLDER = 'OWNER.LINE_2_TOOLTIP_TEXT',
	CITY = 'NEW_APPLICATION.LABEL.CITY',
	ZIPCODE = 'NEW_APPLICATION.LABEL.ZIPCODE',
	COUNTRY = 'NEW_APPLICATION.LABEL.COUNTRY',
	STATE = 'NEW_APPLICATION.LABEL.STATE',
	TERMS = 'PARTNERS.LABEL.TERMS_AND_CONDITIONS_URL',
	MERCHANT_TERMS = 'PARTNERS.LABEL.MERCHANT_TERMS_AND_CONDITIONS',
	TERMS_AND_CONDITIONS = 'PARTNERS.LABEL.TERMS_AND_CONDITIONS',
	NOTIFICATION_SENDER_EMAIL = 'PARTNERS.LABEL.NOTIFICATION_SENDER_EMAIL',
	NOTIFICATION_SENDER_EMAIL_TOOLTIP = 'PARTNERS.LABEL.NOTIFICATION_SENDER_EMAIL_TOOLTIP',
	NOTIFICATION_SENDER_PHONE = 'PARTNERS.LABEL.NOTIFICATION_SENDER_PHONE',
	NOTIFICATION_SENDER_PHONE_TOOLTIP = 'PARTNERS.LABEL.NOTIFICATION_SENDER_PHONE_TOOLTIP',
	REPLY_TO_EMAIL = 'PARTNERS.LABEL.REPLY_TO_EMAIL',
	REPLY_TO_EMAIL_TOOLTIP = 'PARTNERS.LABEL.REPLY_TO_EMAIL_TOOLTIP',
	BCC_EMAIL = 'PARTNERS.LABEL.BCC_EMAIL',
	BCC_EMAIL_TOOLTIP= 'PARTNERS.LABEL.BCC_EMAIL_TOOLTIP',

}
// @ts-ignore
export const getPartnerDetailsSchema = (t: TFunction): Yup.SchemaOf<IPartner> => Yup.object<Shape<IPartner>>().shape({
	[PARTNER_FORM_FIELDS.PARTNER_NAME]: Yup.string()
		.required(t('PARTNERS.VALIDATION.PARTNER_NAME_REQUIRED')),
  [PARTNER_FORM_FIELDS.PARTNER_TYPE]: Yup.string()
		.required(t('PARTNERS.VALIDATION.PARTNER_TYPE_REQUIRED')),
	[PARTNER_FORM_FIELDS.FIRST_NAME]: Yup.string()
		.required(t('PARTNERS.VALIDATION.FIRST_NAME_REQUIRED')),
	[PARTNER_FORM_FIELDS.LAST_NAME]: Yup.string()
		.required(t('PARTNERS.VALIDATION.LAST_NAME_REQUIRED')),
	[PARTNER_FORM_FIELDS.EMAIL]: Yup.string()
		.email(t('PARTNERS.VALIDATION.EMAIL_EXAMPLE'))
		.required(t('PARTNERS.VALIDATION.EMAIL_REQUIRED')),
	[PARTNER_FORM_FIELDS.PHONE]: Yup.number()
		.test('is-valid-phone', t('NEW_APPLICATION.VALIDATION.VALID_PHONE'), (value) => {
      if (!value) return true;
      const phoneRegex = /^[0-9]{10,15}$/;
      return phoneRegex.test(value.toString());
    })
		.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT'))
		.required(t('PARTNERS.VALIDATION.PHONE_REQUIRED')),
	[PARTNER_FORM_FIELDS.SUPPORT_LINK]: Yup.string()
		.url(t('NEW_APPLICATION.VALIDATION.SUPPORT_LINK_FORMAT')),
	[PARTNER_FORM_FIELDS.SUPPORT_EMAIL]: Yup.string()
		.email(t('PARTNERS.VALIDATION.EMAIL_EXAMPLE')),
	[PARTNER_FORM_FIELDS.TERMS]: Yup.string()
	 .url(t('PARTNERS.VALIDATION.TERMS')),
	[PARTNER_FORM_FIELDS.MERCHANT_TERMS]: Yup.string()
	 .url(t('PARTNERS.VALIDATION.TERMS')),
	[PARTNER_FORM_FIELDS.TERMS_AND_CONDITIONS]: Yup.string(),
	[PARTNER_FORM_FIELDS.CONSENT_LABEL]: Yup.string(),
	[PARTNER_FORM_FIELDS.SUPPORT_PHONE]: Yup.number()
		.test('is-valid-phone', t('NEW_APPLICATION.VALIDATION.VALID_PHONE'), (value) => {
      if (!value) return true;
      const phoneRegex = /^[0-9]{10,15}$/;
      return phoneRegex.test(value.toString());
    })
		.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT')),
	[PARTNER_FORM_FIELDS.PRIVACY_POLICY]: Yup.string()
	 .url(t('PARTNERS.VALIDATION.PRIVACY_POLICY')),
	[PARTNER_FORM_FIELDS.SITE_URL]: Yup.string()
	 .url(t('PARTNERS.VALIDATION.PRIVACY_POLICY')),
	[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_EMAIL]: Yup.string()
		.email(t('PARTNERS.VALIDATION.EMAIL_EXAMPLE')),
	[PARTNER_FORM_FIELDS.REPLY_TO_EMAIL]: Yup.string()
		.email(t('PARTNERS.VALIDATION.EMAIL_EXAMPLE')),
	[PARTNER_FORM_FIELDS.BCC_EMAIL]: Yup.string()
		.email(t('PARTNERS.VALIDATION.EMAIL_EXAMPLE')),

});

export const partnerInitialValues: IPartner = {
	[PARTNER_FORM_FIELDS.PARTNER_NAME]: '',
	[PARTNER_FORM_FIELDS.PARTNER_TYPE]: '',
	[PARTNER_FORM_FIELDS.FIRST_NAME]: '',
	[PARTNER_FORM_FIELDS.LAST_NAME]: '',
	[PARTNER_FORM_FIELDS.EMAIL]: '',
	[PARTNER_FORM_FIELDS.PHONE]: '',
	[PARTNER_FORM_FIELDS.SUPPORT_NAME]: '',
	[PARTNER_FORM_FIELDS.SUPPORT_LINK]: '',
	[PARTNER_FORM_FIELDS.SUPPORT_EMAIL]: '',
	[PARTNER_FORM_FIELDS.SUPPORT_PHONE]: '',
	[PARTNER_FORM_FIELDS.LOGO]: '',
	[PARTNER_FORM_FIELDS.PRIVACY_POLICY]: '',
	[PARTNER_FORM_FIELDS.TERMS]: '',
	[PARTNER_FORM_FIELDS.MERCHANT_TERMS]: '',
	[PARTNER_FORM_FIELDS.TERMS_AND_CONDITIONS]: '',
	[PARTNER_FORM_FIELDS.CONSENT_LABEL]: '',
	[PARTNER_FORM_FIELDS.SITE_URL]: '',
	[PARTNER_FORM_FIELDS.LINE_1]: '',
	[PARTNER_FORM_FIELDS.LINE_2]: '',
	[PARTNER_FORM_FIELDS.CITY]: '',
	[PARTNER_FORM_FIELDS.COUNTRY]: 'US',
	[PARTNER_FORM_FIELDS.STATE]: '',
	[PARTNER_FORM_FIELDS.ZIPCODE]: '',
	[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_EMAIL]: '',
	[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_PHONE]: '',
	[PARTNER_FORM_FIELDS.REPLY_TO_EMAIL]: '',
	[PARTNER_FORM_FIELDS.BCC_EMAIL]: '',
};

export const convertToPartnerInitialValues = (data: IPartnerList): IPartner => ({
	[PARTNER_FORM_FIELDS.PARTNER_NAME]: data?.name || '',
	[PARTNER_FORM_FIELDS.PARTNER_TYPE]: data?.type ? data?.type : '',
	[PARTNER_FORM_FIELDS.FIRST_NAME]: data?.mainContact?.firstName || '',
	[PARTNER_FORM_FIELDS.LAST_NAME]: data?.mainContact?.lastName || '',
	[PARTNER_FORM_FIELDS.EMAIL]: data?.mainContact?.email || '',
	[PARTNER_FORM_FIELDS.PHONE]: data?.mainContact?.phone || '',
	[PARTNER_FORM_FIELDS.SUPPORT_NAME]: data?.supportName || '',
	[PARTNER_FORM_FIELDS.SUPPORT_LINK]: data?.supportLink || '',
	[PARTNER_FORM_FIELDS.SUPPORT_EMAIL]: data?.supportEmail || '',
	[PARTNER_FORM_FIELDS.SUPPORT_PHONE]: data?.supportPhone || '',
	[PARTNER_FORM_FIELDS.LOGO]: '',
	[PARTNER_FORM_FIELDS.PRIVACY_POLICY]: data?.privacyPolicyUrl || '',
	[PARTNER_FORM_FIELDS.TERMS]: data?.termsAndConditionsUrl || '',
	[PARTNER_FORM_FIELDS.MERCHANT_TERMS]: data?.merchantTermsAndConditionsUrl || '',
	[PARTNER_FORM_FIELDS.TERMS_AND_CONDITIONS]: data?.termsAndConditions || '',
	[PARTNER_FORM_FIELDS.CONSENT_LABEL]: data?.consentLabel || '',
	[PARTNER_FORM_FIELDS.SITE_URL]: data?.siteUrl || '',
	[PARTNER_FORM_FIELDS.LINE_1]: data?.address?.line1 || '',
	[PARTNER_FORM_FIELDS.LINE_2]: data?.address?.line2 || '',
	[PARTNER_FORM_FIELDS.CITY]: data?.address?.city || '',
	[PARTNER_FORM_FIELDS.COUNTRY]: data?.address?.country || 'US',
	[PARTNER_FORM_FIELDS.STATE]: data?.address?.state || '',
	[PARTNER_FORM_FIELDS.ZIPCODE]: data?.address?.zipCode || '',
	[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_EMAIL]: data?.notificationSenderEmail || '',
	[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_PHONE]: data?.notificationSenderPhone || '',
	[PARTNER_FORM_FIELDS.REPLY_TO_EMAIL]: data?.replyToEmail || '',
	[PARTNER_FORM_FIELDS.BCC_EMAIL]: data?.bccEmail || '',
});

export const getFormattedPartnerFields = (formikValues): IPartnerFormattedFields => ({
	name: formikValues?.[PARTNER_FORM_FIELDS.PARTNER_NAME],
	type: formikValues?.[PARTNER_FORM_FIELDS.PARTNER_TYPE],
	supportName: formikValues?.[PARTNER_FORM_FIELDS.SUPPORT_NAME] ? formikValues?.[PARTNER_FORM_FIELDS.SUPPORT_NAME] : null,
	supportLink: formikValues?.[PARTNER_FORM_FIELDS.SUPPORT_LINK] ? formikValues?.[PARTNER_FORM_FIELDS.SUPPORT_LINK] : null,
	supportEmail: formikValues?.[PARTNER_FORM_FIELDS.SUPPORT_EMAIL] ? formikValues?.[PARTNER_FORM_FIELDS.SUPPORT_EMAIL] : null,
	supportPhone: formikValues?.[PARTNER_FORM_FIELDS.SUPPORT_PHONE] ? formikValues?.[PARTNER_FORM_FIELDS.SUPPORT_PHONE] : null,
	logoFile: formikValues?.[PARTNER_FORM_FIELDS.LOGO],
	privacyPolicyUrl: formikValues?.[PARTNER_FORM_FIELDS.PRIVACY_POLICY] ? formikValues?.[PARTNER_FORM_FIELDS.PRIVACY_POLICY] : null,
	termsAndConditionsUrl: formikValues?.[PARTNER_FORM_FIELDS.TERMS] ? formikValues?.[PARTNER_FORM_FIELDS.TERMS] : null,
	merchantTermsAndConditionsUrl: formikValues?.[PARTNER_FORM_FIELDS.MERCHANT_TERMS] ? formikValues?.[PARTNER_FORM_FIELDS.MERCHANT_TERMS] : null,
	termsAndConditions: formikValues?.[PARTNER_FORM_FIELDS.TERMS_AND_CONDITIONS] ? formikValues?.[PARTNER_FORM_FIELDS.TERMS_AND_CONDITIONS] : null,
	consentLabel: formikValues?.[PARTNER_FORM_FIELDS.CONSENT_LABEL] ? formikValues?.[PARTNER_FORM_FIELDS.CONSENT_LABEL] : null,
	siteUrl: formikValues?.[PARTNER_FORM_FIELDS.SITE_URL],
	notificationSenderEmail: formikValues?.[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_EMAIL] ? formikValues?.[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_EMAIL] : null,
	notificationSenderPhone: formikValues?.[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_PHONE] ? formikValues?.[PARTNER_FORM_FIELDS.NOTIFICATION_SENDER_PHONE] : null,
	replyToEmail: formikValues?.[PARTNER_FORM_FIELDS.REPLY_TO_EMAIL] ? formikValues?.[PARTNER_FORM_FIELDS.REPLY_TO_EMAIL] : null,
	bccEmail: formikValues?.[PARTNER_FORM_FIELDS.BCC_EMAIL] ? formikValues?.[PARTNER_FORM_FIELDS.BCC_EMAIL] : null,
	mainContact: {
		firstName: formikValues?.[PARTNER_FORM_FIELDS.FIRST_NAME],
		lastName: formikValues?.[PARTNER_FORM_FIELDS.LAST_NAME],
		phone: formikValues?.[PARTNER_FORM_FIELDS.PHONE],
		email: formikValues?.[PARTNER_FORM_FIELDS.EMAIL],
	},
	address: {
		line1: formikValues?.[PARTNER_FORM_FIELDS.LINE_1],
		line2: formikValues?.[PARTNER_FORM_FIELDS.LINE_2],
		city: formikValues?.[PARTNER_FORM_FIELDS.CITY],
		country: formikValues?.[PARTNER_FORM_FIELDS.COUNTRY],
		state: isUSCountry(formikValues?.[PARTNER_FORM_FIELDS.COUNTRY]) ? formikValues?.[PARTNER_FORM_FIELDS.STATE] : null,
		zipCode: formikValues?.[PARTNER_FORM_FIELDS.ZIPCODE],
	}
});

export const getFormattedInitialPartnerFields = (data: IPartnerList): IPartnerFormattedFields => ({
	name: data?.name,
	type: data?.type,
	supportName: data?.supportName,
	supportLink: data?.supportLink,
	supportEmail: data?.supportEmail,
	supportPhone: data?.supportPhone,
	logoUrl: data?.logoUrl,
	privacyPolicyUrl: data?.privacyPolicyUrl,
	termsAndConditionsUrl: data?.termsAndConditionsUrl,
	merchantTermsAndConditionsUrl: data?.merchantTermsAndConditionsUrl,
	termsAndConditions: data?.termsAndConditions,
	consentLabel: data?.consentLabel,
	siteUrl: data?.siteUrl,
	notificationSenderEmail: data?.notificationSenderEmail,
	notificationSenderPhone: data?.notificationSenderPhone,
	replyToEmail: data?.replyToEmail,
	bccEmail: data?.bccEmail,
	mainContact: {
		firstName: data?.mainContact?.firstName,
		lastName: data?.mainContact?.lastName,
		email: data?.mainContact?.email,
		phone: data?.mainContact?.phone,
	},
	address: {
		line1: data?.address?.line1,
		line2: data?.address?.line2,
		city: data?.address?.city,
		country: data?.address?.country,
		state: data?.address?.state,
		zipCode: data?.address?.zipCode,
	}
});
